/**
 * Support Widget 集成 Hook
 * @description 管理 Holopix 智能客服悬浮球组件的生命周期和状态
 * @module useSupportWidget
 *
 * 注意：@xmiles/support-widget 是 React 组件库，通过 UMD 方式接入 Vue 项目
 * 全局变量：window.HolopixSupport
 *
 * 功能：
 * 1. 自动挂载/卸载 widget
 * 2. 响应式更新用户上下文
 * 3. 处理教程卡片点击导航
 */

import { computed, onMounted, onUnmounted, ref, watch, type Ref } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/store/user";
import { storeToRefs } from "pinia";
import { canShowSupportWidget } from "./supportWidgetGate";
import { buildSupportWidgetContext, clearSupportLastError, getSupportLastErrorRef, type SupportWidgetContextPayload } from "./supportWidgetContext";
import { createSupportWidgetMountLock } from "./supportWidgetMountLock";
import {
  isSupportAssistantEnabled,
  subscribeSupportAssistantVisibility
} from "./supportWidgetVisibility";

/** 脚本加载 Promise 单例，避免并发 mount 重复插入 script 标签 */
let widgetAssetsPromise: Promise<void> | null = null;

export type Context = SupportWidgetContextPayload;

export interface TutorialCard {
  articleId: string;
  title: string;
  icon?: string;
}

export interface WidgetInstance {
  setContext: (context: Partial<Context>) => void;
  setEnabled?: (enabled: boolean) => void;
  unmount: () => void;
}

/**
 * 全局 HolopixSupport 对象声明
 */
declare global {
  interface Window {
    HolopixSupport?: {
      mountHolopixSupport: (options?: { context?: Context; onTutorialClick?: (card: TutorialCard) => void; apiBase?: string }) => WidgetInstance;
      configureApi: (base: string) => void;
      setAssistantEnabled?: (enabled: boolean) => void;
      isAssistantEnabled?: () => boolean;
      Widget?: any;
      useChat?: any;
      MessageCards?: any;
    };
  }
}

/**
 * Widget 配置选项
 */
export interface UseSupportWidgetOptions {
  /** API 基础地址（可选，默认使用生产环境） */
  apiBase?: string;
  /** 是否自动挂载（默认 true） */
  autoMount?: boolean;
}

/**
 * Support Widget Hook
 */
export function useSupportWidget(options: UseSupportWidgetOptions = {}) {
  const { apiBase, autoMount = true } = options;

  const widgetRef: Ref<WidgetInstance | null> = ref(null);
  const mountLock = createSupportWidgetMountLock();
  const router = useRouter();
  const userStore = useUserStore();
  const { userInfo, isLogin, highestPackage, isGroupLogin, sources, packages } = storeToRefs(userStore);
  const lastErrorRef = getSupportLastErrorRef();
  let stopShowWatch: (() => void) | undefined;
  /** 用户偏好：是否显示客服助手（与 Widget 停用助手共用 localStorage） */
  const assistantPrefEnabled = ref(isSupportAssistantEnabled());

  /** 是否展示悬浮球（登录 + 用户未停用助手） */
  const canShowWidget = computed(
    () => canShowSupportWidget({ isLogin: isLogin.value }) && assistantPrefEnabled.value
  );

  /** 从当前路由解析页面名 */
  function resolvePageName() {
    const route = router.currentRoute.value;
    return (route.meta?.label || route.meta?.title || route.name) as string | undefined;
  }

  /** 构建 Widget Context */
  function buildContext(): Context {
    return buildSupportWidgetContext({
      userStore,
      page: resolvePageName()
    });
  }

  /** 同步完整 context 到已挂载的 widget */
  function syncContextToWidget() {
    if (widgetRef.value && isLogin.value) {
      widgetRef.value.setContext(buildContext());
    }
  }

  function loadWidgetAssets(): Promise<void> {
    if (window.HolopixSupport?.mountHolopixSupport) {
      return Promise.resolve();
    }
    if (widgetAssetsPromise) {
      return widgetAssetsPromise;
    }

    const pending = new Promise<void>((resolve, reject) => {
      // 频道目录：latest（开发/测试）或 latest-prod（正式/灰度），由这里拼 CDN URL
      const version = import.meta.env.VITE_SUPPORT_WIDGET_VERSION as string | undefined;
      if (!version) {
        console.error("[useSupportWidget] VITE_SUPPORT_WIDGET_VERSION 未配置");
        reject(new Error("[useSupportWidget] VITE_SUPPORT_WIDGET_VERSION 未配置"));
        return;
      }

      const src = `https://static.holopix.cn/projects/npm/holopix-support/umd/${version}/holopix-widget.umd.js`;
      const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
      if (existing) {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () => reject(new Error("[useSupportWidget] 加载 holopix-widget.umd.js 失败")));
        return;
      }

      const script = document.createElement("script");
      script.src = src;
      script.crossOrigin = "anonymous";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("[useSupportWidget] 加载 holopix-widget.umd.js 失败"));
      document.head.appendChild(script);
    });

    widgetAssetsPromise = pending;
    pending.catch(() => {
      if (widgetAssetsPromise === pending) {
        widgetAssetsPromise = null;
      }
    });

    return pending;
  }

  async function mountWidget() {
    if (widgetRef.value) {
      console.warn("[useSupportWidget] Widget 已挂载，跳过重复挂载");
      return;
    }

    const token = mountLock.begin();
    if (token == null) {
      return;
    }

    try {
      await loadWidgetAssets();

      // 等待脚本期间可能已卸载，丢弃过期挂载
      if (!mountLock.isCurrent(token) || widgetRef.value) {
        return;
      }

      if (!window.HolopixSupport?.mountHolopixSupport) {
        console.error("[useSupportWidget] HolopixSupport 未加载");
        return;
      }

      if (apiBase && window.HolopixSupport.configureApi) {
        window.HolopixSupport.configureApi(apiBase);
      }

      widgetRef.value = window.HolopixSupport.mountHolopixSupport({
        context: buildContext(),
        apiBase,
      });
    } catch (error) {
      console.error("[useSupportWidget] 挂载失败:", error);
    } finally {
      mountLock.end(token);
    }
  }

  function unmountWidget() {
    mountLock.invalidate();
    if (widgetRef.value) {
      widgetRef.value.unmount();
      widgetRef.value = null;
    }
    clearSupportLastError();
  }

  function setContext(context: Partial<Context>) {
    widgetRef.value?.setContext(context);
  }

  // sources/packages 变化时同步算力、签约、计费周期等画像字段
  watch([isLogin, userInfo, highestPackage, isGroupLogin, sources, packages], syncContextToWidget, { deep: true });

  watch(lastErrorRef, () => {
    if (widgetRef.value && isLogin.value) {
      widgetRef.value.setContext(buildContext());
    }
  });

  watch(isLogin, (loggedIn) => {
    if (!loggedIn) {
      clearSupportLastError();
    }
  });

  watch(
    () => router.currentRoute.value,
    (route) => {
      if (widgetRef.value && route) {
        const pageName = (route.meta?.label || route.meta?.title || route.name) as string;
        if (pageName) {
          widgetRef.value.setContext({ page: pageName });
        }
      }
    }
  );

  if (autoMount) {
    onMounted(() => {
      assistantPrefEnabled.value = isSupportAssistantEnabled();
      const stopPref = subscribeSupportAssistantVisibility((enabled) => {
        assistantPrefEnabled.value = enabled;
      });

      // 仅在 onMounted 后启动 watch，避免与 onMounted 各调一次 mount 形成竞态
      stopShowWatch = watch(
        canShowWidget,
        (show) => {
          if (show) {
            mountWidget();
          } else {
            unmountWidget();
          }
        },
        { immediate: true }
      );

      const stopMount = stopShowWatch;
      stopShowWatch = () => {
        stopPref();
        stopMount();
      };
    });

    onUnmounted(() => {
      stopShowWatch?.();
      stopShowWatch = undefined;
      unmountWidget();
    });
  }

  return {
    widgetRef,
    mountWidget,
    unmountWidget,
    setContext
  };
}

export default useSupportWidget;
